import { useState, useEffect } from "react";
import {
  getTagResourceCounts,
  getDevicesByTag,
  getAppsByTag,
  getPoliciesByTag,
} from "@/services/tagsApi";

export interface TagResourceCounts {
  devices: number;
  profiles: number;
  applications: number;
  configurations: number;
  patchPolicies: number;
  users: number;
  total: number;
}

/**
 * Resolves resource counts for a tag.
 *
 * The /resources/count endpoint reliably reports counts for **dynamic** tags
 * (driven by criteria) but returns 0 for static tags whose memberships live
 * in the per-resource listing endpoints. To avoid the empty `Devices: 0` we
 * saw on static tags, fall back to the per-resource listing endpoints when
 * the aggregated count returns nothing — they're authoritative for both
 * static and dynamic tags. Listing calls run in parallel and only when the
 * fast aggregate path fails.
 */
export const useTagResourceCounts = (tagId: string) => {
  const [counts, setCounts] = useState<TagResourceCounts>({
    devices: 0, profiles: 0, applications: 0, configurations: 0, patchPolicies: 0, users: 0, total: 0
  });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchResourceCounts = async () => {
    if (!tagId) return;
    try {
      setIsLoading(true);
      setError(null);

      // First try the cheap aggregated endpoint.
      let deviceCount = 0;
      let appCount = 0;
      let policyCount = 0;
      try {
        const data = await getTagResourceCounts(tagId);
        deviceCount = (data as any).devices ?? (data as any).device_count ?? (data as any).deviceCount ?? 0;
        appCount = (data as any).apps ?? (data as any).applications ?? (data as any).app_count ?? (data as any).appCount ?? 0;
        policyCount = (data as any).policies ?? (data as any).profiles ?? (data as any).policy_count ?? (data as any).policyCount ?? 0;
      } catch {
        // Aggregate endpoint failed — listing endpoints will provide the truth below.
      }

      // For static tags the aggregate endpoint returns zeros; verify against the
      // per-resource listing endpoints whenever we got nothing back, so the badge
      // reflects the actual membership instead of "0".
      if (deviceCount === 0 && appCount === 0 && policyCount === 0) {
        const [devs, apps, pols] = await Promise.all([
          getDevicesByTag(tagId).catch(() => []),
          getAppsByTag(tagId).catch(() => []),
          getPoliciesByTag(tagId).catch(() => []),
        ]);
        deviceCount = Array.isArray(devs) ? devs.length : 0;
        appCount = Array.isArray(apps) ? apps.length : 0;
        policyCount = Array.isArray(pols) ? pols.length : 0;
      }

      setCounts({
        devices: deviceCount,
        profiles: policyCount,
        applications: appCount,
        configurations: 0,
        patchPolicies: 0,
        users: 0,
        total: deviceCount + appCount + policyCount,
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch resource counts');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchResourceCounts();
  }, [tagId]);

  return { counts, isLoading, error, refetch: fetchResourceCounts };
};
